import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type InterviewStatus = 'booked' | 'cancelled';

export interface InterviewRow {
  /**
   * Non-credential handle for this booking (a digest). The candidate's TOKEN deliberately never
   * reaches this client: the booking id IS the manage-page credential, and this list is served to
   * every signed-in employee — returning it let anyone act as any candidate.
   */
  bookingRef: string;
  status: InterviewStatus;
  /** For a cancelled interview this is the time it *had* been booked for. */
  start: string | null;
  end: string | null;
  minutes: number | null;
  candidateName: string | null;
  candidateEmail: string | null;
  jobTitle: string | null;
  /** The Lever posting behind the interview link. Not a credential. Optional: older proxies omit it. */
  postingId?: string | null;
  /** FINN department from the posting's tags / team category, for the Log's filter. Optional. */
  department?: string | null;
  stageName: string | null;
  taOwner: string | null;
  linkId: string | null;
  /** Who actually got it — for a 1:1 link that is one of several configured. */
  interviewers: string[];
  /**
   * '1:1' | 'panel' — from the interview link. Null for a booking with no link (e.g. a final
   * round). The Log offers "Replace interviewer" on 1:1 bookings only. Absent on older proxies.
   */
  meetingType?: '1:1' | 'panel' | null;
  meetLink: string | null;
  htmlLink: string | null;
  leverPanelId: string | null;
  opportunityId: string | null;
  bookedAt: string | null;
  rescheduleCount: number;
  rescheduledAt: string | null;
  rescheduleReason: string | null;
  /**
   * Who moved it and from which side — 'candidate' on their own page, 'finn' from this Log,
   * 'calendar' when a recruiter moved the event by hand in Google Calendar and the sync caught up.
   */
  rescheduledBy: string | null;
  rescheduleSource: 'candidate' | 'finn' | 'calendar' | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  cancelledBy: string | null;
  cancelSource: 'candidate' | 'finn' | null;
  /**
   * Whether Lever reflects this booking's current state. `'failed'` means our record and the
   * candidate moved on and Lever did not — the one value worth putting in front of a recruiter.
   */
  leverSync: 'updated' | 'created' | 'cancelled' | 'skipped' | 'failed' | null;
  leverSyncError: string | null;
  /** 'linkedin-share' when booked through a LinkedIn link rather than an invite. Absent on an older proxy. */
  source?: 'linkedin-share' | null;
  /**
   * The calendar sync found this booking's event GONE from Google Calendar while the record is
   * still live. A flag for a human — nothing is cancelled automatically. Absent on an older proxy.
   */
  calendarEventMissing?: boolean;
  /** Server-computed: still ahead of us. Kept off the client so render stays pure. */
  upcoming: boolean;
}

/** The staff cancel vocabulary — mirrors STAFF_CANCEL_REASONS on the proxy. */
export const STAFF_CANCEL_REASONS = [
  { code: 'candidate_requested', label: 'The candidate asked us to cancel' },
  { code: 'interviewer_unavailable', label: 'The interviewer is no longer available' },
  { code: 'role_closed', label: 'The role was closed or put on hold' },
  { code: 'other', label: 'Other' },
] as const;

/**
 * FINN cancels a booking, as FINN — the staff route, keyed by the non-credential ref.
 *
 * A 409 means someone got there first (the candidate on their own page, or a colleague); retrying
 * is what would send a second cancellation email, so it surfaces as `alreadyCancelled` rather than
 * an error to retry.
 */
export async function cancelInterviewBooking(
  bookingRef: string,
  body: { reasonCode: string; note?: string } = { reasonCode: 'candidate_requested' }
): Promise<{ ok: boolean; cancelled: boolean; alreadyCancelled?: boolean; emailSent?: boolean; demo?: boolean }> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/cancel`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      suppressErrorStatuses: [409],
    }
  );
  if (res.status === 409) {
    /**
     * Two different 409s: the claim was lost (body carries alreadyCancelled — the booking IS
     * cancelled, retire the UI) versus the fake-calendar refusal (dev-only; the booking is STILL
     * LIVE and must not be reported as cancelled). Collapsing them showed "already cancelled" for
     * an interview that was never touched.
     */
    const detail = (await res.json().catch(() => null)) as
      | { error?: string; alreadyCancelled?: boolean }
      | null;
    if (detail?.alreadyCancelled) return { ok: false, cancelled: false, alreadyCancelled: true };
    throw new Error(detail?.error || 'Could not cancel the interview.');
  }
  return res.json() as Promise<{ ok: boolean; cancelled: boolean; emailSent?: boolean; demo?: boolean }>;
}

/**
 * The staff reschedule vocabulary — mirrors STAFF_RESCHEDULE_REASONS on the proxy.
 *
 * NONE of these is emailed to the candidate: they land on the calendar event and in the audit
 * trail. The candidate-facing sentence is the separate `messageToCandidate` field, which is why
 * these can say things like "the panel changed".
 */
export const STAFF_RESCHEDULE_REASONS = [
  { code: 'candidate_requested', label: 'The candidate asked us to move it' },
  { code: 'interviewer_unavailable', label: 'The interviewer can no longer make it' },
  { code: 'panel_changed', label: 'The panel or format changed' },
  { code: 'process_delay', label: 'Something earlier in the process slipped' },
  { code: 'other', label: 'Other' },
] as const;

export interface BookingSlots {
  slots: { start: string; end: string }[];
  /** Why the list is empty, when it is — an empty picker with no explanation reads as a bug. */
  slotError: string | null;
  minutes: number | null;
  currentStart: string | null;
}

/** The free slots a staff reschedule may pick from — the same ones the candidate's page offers. */
export async function fetchBookingSlots(bookingRef: string): Promise<BookingSlots> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/slots`
  );
  if (!res.ok) throw new Error('Could not load available times');
  return (await res.json()) as BookingSlots;
}

/**
 * FINN moves a booking, as FINN — keyed by the non-credential ref, like the cancel route.
 *
 * `override: true` means the recruiter typed a time instead of picking a free slot, and the server
 * skips the free/busy check. A 409 is terminal: either someone got there first, or this is a second
 * click, and retrying is what would send the candidate a second email naming a different time.
 */
export async function rescheduleInterviewBooking(
  bookingRef: string,
  body: {
    start: string;
    reasonCode: string;
    note?: string;
    messageToCandidate?: string;
    /** The whole candidate-facing email, pre-filled from the Email Library and edited here. */
    body?: string;
    language?: 'de' | 'en';
    /** Omitted when the recruiter leaves "email the candidate" ticked; `false` suppresses the email. */
    sendEmail?: boolean;
    override?: boolean;
  }
): Promise<{
  ok: boolean;
  moved: boolean;
  checked?: boolean;
  booking?: { start: string; end: string };
  emailSent?: boolean;
  calendar?: string;
  lever?: string;
  demo?: boolean;
  alreadyMoved?: boolean;
}> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/reschedule`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      suppressErrorStatuses: [409],
    }
  );
  if (res.status === 409) {
    const detail = (await res.json().catch(() => null)) as
      | { error?: string; alreadyMoved?: boolean }
      | null;
    if (detail?.alreadyMoved) return { ok: false, moved: false, alreadyMoved: true };
    // Slot taken, booking no longer live, or the fake-calendar refusal — all mean the interview is
    // NOT at the new time, so they surface as errors rather than a silent success.
    throw new Error(detail?.error || 'Could not move the interview.');
  }
  return res.json() as Promise<{ ok: boolean; moved: boolean }>;
}

/** Why a replacement interviewer is flagged as not free — advisory, never blocking. */
export type ReplacementReason = 'busy' | 'outside_availability' | 'calendar_unreadable' | null;

export interface ReplacementInterviewer {
  email: string;
  name: string;
  /** true = free, false = busy / at daily limit / outside their window, null = calendar unreadable. */
  available: boolean | null;
  reason: ReplacementReason;
}

export interface ReplacementPool {
  /** '1:1' supports a swap; 'panel' (or null link) does not. */
  meetingType: '1:1' | 'panel' | null;
  movable: boolean;
  current: { email: string; name: string | null } | null;
  candidates: ReplacementInterviewer[];
  slotError?: string | null;
  demo?: boolean;
}

/**
 * The interviewer pool a booked 1:1 interview may be swapped to, each judged against the booking's
 * existing time so the dialog can warn. Read-only; nothing is changed.
 */
export async function fetchReplacementInterviewers(bookingRef: string): Promise<ReplacementPool> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/interviewers`
  );
  if (!res.ok) throw new Error('Could not load the interviewer pool');
  return (await res.json()) as ReplacementPool;
}

/**
 * FINN swaps the interviewer on a booked 1:1 interview — the time is unchanged and the candidate is
 * NOT emailed. `override: true` confirms a replacement the calendars call busy; without it the server
 * refuses a busy pick with a 409 `clash` so the dialog can ask first.
 */
export async function replaceInterviewer(
  bookingRef: string,
  body: { toEmail: string; override?: boolean }
): Promise<{
  ok: boolean;
  replaced: boolean;
  override?: boolean;
  interviewers?: string[];
  calendar?: string;
  lever?: string;
  demo?: boolean;
  /** The chosen interviewer is busy and override was not set — the dialog asks, then retries with override. */
  clash?: boolean;
  reason?: ReplacementReason;
}> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/replace-interviewer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      suppressErrorStatuses: [409],
    }
  );
  if (res.status === 409) {
    const detail = (await res.json().catch(() => null)) as
      | { error?: string; clash?: boolean; reason?: ReplacementReason }
      | null;
    // A clash is not an error to surface as a failure — it is the "confirm anyway?" prompt.
    if (detail?.clash) return { ok: false, replaced: false, clash: true, reason: detail.reason ?? null };
    // Already swapped, no longer live, a panel, a stale dialog, or the fake-calendar refusal — the
    // interview is NOT at the new interviewer, so these surface as errors rather than a silent success.
    throw new Error(detail?.error || 'Could not change the interviewer.');
  }
  return res.json() as Promise<{ ok: boolean; replaced: boolean }>;
}

export interface InterviewsSummary {
  total: number;
  booked: number;
  cancelled: number;
  rescheduled: number;
  upcoming: number;
}

export async function fetchInterviews(): Promise<{
  interviews: InterviewRow[];
  summary: InterviewsSummary;
}> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/interviews`);
  if (!res.ok) throw new Error('Could not load interviews');
  return (await res.json()) as { interviews: InterviewRow[]; summary: InterviewsSummary };
}

/** What the interviewers' calendars say about a time a candidate suggested. */
export interface SuggestionCheck {
  /** `free` = the interview fits in the range; `clash` = it does not; `unknown` = a calendar could not be read. */
  status: 'free' | 'clash' | 'unknown';
  /** 1:1 links only: how many of the interviewers could take it. */
  freeCount?: number;
  total: number;
}

/** A time a candidate suggested on the booking page ("None of these work?"). Nothing is booked for it. */
export interface TimeSuggestion {
  id: string;
  candidateName: string | null;
  candidateEmail: string | null;
  jobTitle: string | null;
  stageName: string | null;
  minutes: number | null;
  meetingType: '1:1' | 'panel' | null;
  /** The recruiter the invite was sent by — who was emailed. */
  owner: string | null;
  interviewers: string[];
  /** "Tuesday 3 November 2026, 14:00–16:00 (Berlin time)" */
  when: string;
  note: string;
  createdAt: string;
  /** The range has already ended — nothing left to agree, only to dismiss. */
  past: boolean;
  /** Null when `past`. */
  check: SuggestionCheck | null;
}

export async function fetchTimeSuggestions(): Promise<TimeSuggestion[]> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/interviews/time-suggestions`);
  if (!res.ok) throw new Error('Could not load suggested times');
  return ((await res.json()) as { suggestions: TimeSuggestion[] }).suggestions;
}

/** The recruiter has answered the candidate; the card goes away. Idempotent. */
export async function markTimeSuggestionHandled(id: string): Promise<void> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interviews/time-suggestions/${encodeURIComponent(id)}/handled`,
    { method: 'POST', suppressErrorStatuses: [404] }
  );
  // 404 = already gone (purged, or handled by a colleague): the card should disappear either way.
  if (!res.ok && res.status !== 404) throw new Error('Could not mark it as handled');
}

/**
 * What the candidate would receive if the interview moved to `start` — the SAME
 * `buildBookingEmail({ kind: 'rescheduled' })` the move itself sends, so the preview cannot drift
 * from the real email. Nothing is sent or changed.
 */
export async function previewRescheduleEmail(
  bookingRef: string,
  start: string,
  candidateName?: string
): Promise<{ subject: string; text: string; to?: string | null; willSend?: boolean; demo?: boolean }> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/reschedule/preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start, candidateName }),
    }
  );
  return res.json() as Promise<{
    subject: string;
    text: string;
    to?: string | null;
    willSend?: boolean;
    demo?: boolean;
  }>;
}

/**
 * The wording the reschedule dialog starts from — the Email Library's
 * `checking-in` / `reschedule` template, rendered for this booking.
 *
 * `body` is null when TA deactivated or emptied the template; the dialog then
 * opens with an empty box and the email falls back to its own wording, which is
 * exactly what happened before this template existed. Never throws for that
 * reason: a missing default must not stop someone fixing a calendar clash.
 *
 * The returned `language` is the one the body is ACTUALLY in, which is not always
 * the one asked for — see the fallback in rescheduleMessage.js.
 */
export async function fetchRescheduleDefaultMessage(
  bookingRef: string,
  /**
   * Omitted until the recruiter picks one — the proxy then derives the default
   * from the interview link (set per role+stage), so an English pipeline opens
   * in English with no click. Naming a language here OVERRIDES that.
   */
  language?: 'de' | 'en'
): Promise<{ body: string | null; language: 'de' | 'en' }> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}` +
      `/reschedule/default-message${language ? `?language=${language}` : ''}`
  );
  // No language was asked for and the read failed, so there is nothing to
  // resolve: 'en' is what this route fell back to before it derived anything.
  if (!res.ok) return { body: null, language: language ?? 'en' };
  // `language` comes back RESOLVED: an empty German template yields the English
  // wording, and the email's frame has to follow the words, not the request.
  return (await res.json()) as { body: string | null; language: 'de' | 'en' };
}
